import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Star, MapPin, Briefcase, Globe, Linkedin, Clock, ArrowRight, ChevronLeft, BookOpen, CheckCircle } from "lucide-react";
import Navbar from "@/components/Navbar";
import { API } from "@/context/AuthContext";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export default function ExpertProfile() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("about");
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    const fetch_ = async () => {
      try {
        const res = await fetch(`${API}/experts/${userId}`);
        if (!res.ok) throw new Error("Not found");
        const data = await res.json();
        setProfile(data);
      } catch {
        navigate("/discover");
      } finally {
        setLoading(false);
      }
    };
    fetch_();
  }, [userId]);

  const handleBook = (service) => {
    if (!user) {
      navigate("/login");
      return;
    }
    navigate(`/book/${service.service_id}?expert=${userId}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFAF8]">
        <Navbar />
        <div className="pt-24 flex items-center justify-center">
          <div className="w-8 h-8 border-2 border-[#0F172A] border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  if (!profile) return null;

  const avgRating = profile.reviews?.length > 0
    ? (profile.reviews.reduce((s, r) => s + r.rating, 0) / profile.reviews.length).toFixed(1)
    : profile.rating;

  return (
    <div className="min-h-screen bg-[#FAFAF8]">
      <Navbar />
      <div className="pt-20">
        {/* Back */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6">
          <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-sm text-[#52525B] hover:text-[#0F172A] transition-colors mb-6">
            <ChevronLeft size={16} /> Back to experts
          </button>
        </div>

        {/* Profile header */}
        <div className="bg-white border-b border-[#E4E4E1]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
            <div className="flex flex-col md:flex-row gap-8 items-start">
              <div className="relative shrink-0">
                <img
                  src={profile.profile_image || `https://ui-avatars.com/api/?name=${encodeURIComponent(profile.name || "")}&background=0F172A&color=fff&size=200`}
                  alt={profile.name}
                  className="w-32 h-32 rounded-sm object-cover border border-[#E4E4E1]"
                />
                {profile.is_verified && (
                  <div className="absolute -bottom-2 -right-2 bg-[#064E3B] text-white rounded-full p-1">
                    <CheckCircle size={16} />
                  </div>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div>
                    {profile.is_featured && (
                      <span className="section-label text-[#064E3B] mb-2 block">Featured Expert</span>
                    )}
                    <h1 className="text-3xl font-normal text-[#0F172A] mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>
                      {profile.name}
                    </h1>
                    <p className="text-[#52525B] text-base mb-3">{profile.headline}</p>
                    <div className="flex flex-wrap items-center gap-4 text-sm text-[#A1A1AA]">
                      {profile.location && (
                        <span className="flex items-center gap-1"><MapPin size={14} />{profile.location}</span>
                      )}
                      {profile.years_experience > 0 && (
                        <span className="flex items-center gap-1"><Briefcase size={14} />{profile.years_experience} years experience</span>
                      )}
                      {profile.total_reviews > 0 && (
                        <span className="flex items-center gap-1">
                          <Star size={14} className="text-amber-500 fill-amber-500" />
                          {avgRating} ({profile.total_reviews} reviews)
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    {profile.linkedin_url && (
                      <a href={profile.linkedin_url} target="_blank" rel="noopener noreferrer" className="p-2 border border-[#E4E4E1] rounded-sm hover:border-[#0F172A] transition-colors">
                        <Linkedin size={16} className="text-[#0F172A]" />
                      </a>
                    )}
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 mt-4">
                  {profile.industries?.slice(0, 4).map((i) => (
                    <span key={i} className="text-xs bg-[#F4F4F1] text-[#52525B] px-3 py-1 rounded-sm font-medium">{i}</span>
                  ))}
                  {profile.expertise?.slice(0, 3).map((e) => (
                    <span key={e} className="text-xs bg-[#064E3B]/10 text-[#064E3B] px-3 py-1 rounded-sm font-medium">{e}</span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Main content */}
            <div className="lg:col-span-2">
              {/* Tabs */}
              <div className="flex gap-0 border border-[#E4E4E1] rounded-sm overflow-hidden mb-6 w-fit">
                {[
                  { id: "about", label: "About" },
                  { id: "reviews", label: `Reviews (${profile.total_reviews || 0})` },
                  { id: "articles", label: `Articles (${profile.articles?.length || 0})` },
                ].map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`px-5 py-2.5 text-sm font-medium transition-colors ${activeTab === tab.id ? "bg-[#0F172A] text-white" : "bg-white text-[#52525B] hover:bg-[#F4F4F1]"}`}
                    data-testid={`tab-${tab.id}`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>

              {activeTab === "about" && (
                <div className="space-y-6">
                  <div className="card-flat">
                    <h3 className="text-lg font-medium text-[#0F172A] mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>About</h3>
                    <p className="text-[#52525B] text-sm leading-relaxed">{profile.bio || "No bio added yet."}</p>
                  </div>
                  {profile.current_role && (
                    <div className="card-flat">
                      <h3 className="text-sm font-semibold text-[#0F172A] mb-3 uppercase tracking-wider">Current Role</h3>
                      <div className="flex items-center gap-3">
                        <Briefcase size={16} className="text-[#A1A1AA]" />
                        <div>
                          <p className="text-sm font-medium text-[#0F172A]">{profile.current_role}</p>
                          <p className="text-xs text-[#52525B]">{profile.current_org}</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {activeTab === "reviews" && (
                <div className="space-y-4">
                  {(profile.reviews || []).length === 0 ? (
                    <div className="card-flat text-center py-8">
                      <p className="text-[#52525B] text-sm">No reviews yet.</p>
                    </div>
                  ) : (
                    profile.reviews.map((rev) => (
                      <div key={rev.review_id} className="card-flat">
                        <div className="flex items-center justify-between mb-3">
                          <p className="font-medium text-sm text-[#0F172A]">{rev.explorer_name || "Explorer"}</p>
                          <div className="flex">
                            {[...Array(rev.rating)].map((_, i) => (
                              <Star key={`${rev.review_id || rev.explorer_name}-star-${i}`} size={12} className="text-amber-500 fill-amber-500" />
                            ))}
                          </div>
                        </div>
                        <p className="text-sm text-[#52525B] leading-relaxed">{rev.review_text}</p>
                      </div>
                    ))
                  )}
                </div>
              )}

              {activeTab === "articles" && (
                <div className="space-y-4">
                  {(profile.articles || []).length === 0 ? (
                    <div className="card-flat text-center py-8">
                      <p className="text-[#52525B] text-sm">No articles published yet.</p>
                    </div>
                  ) : (
                    profile.articles.map((art) => (
                      <div key={art.article_id} className="card-flat hover:border-[#0F172A] transition-colors cursor-pointer">
                        <h4 className="font-medium text-[#0F172A] mb-2">{art.title}</h4>
                        <p className="text-sm text-[#52525B] line-clamp-2">{art.summary}</p>
                        <div className="flex gap-2 mt-3">
                          {(art.tags || []).slice(0, 3).map((t) => (
                            <span key={t} className="text-xs bg-[#F4F4F1] text-[#52525B] px-2 py-0.5 rounded-sm">{t}</span>
                          ))}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>

            {/* Sidebar - Services */}
            <div className="space-y-4">
              <div className="card-flat">
                <h3 className="text-sm font-semibold text-[#0F172A] mb-4 uppercase tracking-wider">Book a Session</h3>
                {(profile.services || []).length === 0 ? (
                  <p className="text-sm text-[#A1A1AA]">No services available</p>
                ) : (
                  <div className="space-y-3">
                    {profile.services.map((svc) => (
                      <div key={svc.service_id} className="border border-[#E4E4E1] rounded-sm p-4 hover:border-[#0F172A] transition-colors">
                        <div className="flex justify-between items-start mb-1">
                          <h4 className="text-sm font-semibold text-[#0F172A]">{svc.name}</h4>
                          <span className="text-sm font-bold text-[#0F172A]">₹{svc.price?.toLocaleString("en-IN")}</span>
                        </div>
                        <div className="flex items-center gap-1 text-xs text-[#A1A1AA] mb-3">
                          <Clock size={11} /> {svc.duration} mins
                        </div>
                        <p className="text-xs text-[#52525B] mb-3">{svc.description}</p>
                        <button
                          onClick={() => handleBook(svc)}
                          className="w-full btn-primary text-sm py-2 flex items-center justify-center gap-1"
                          data-testid={`book-service-${svc.service_id}`}
                        >
                          Book Now <ArrowRight size={13} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              {profile.availability_status && (
                <div className="flex items-center gap-2 px-4 py-3 bg-[#064E3B]/5 border border-[#064E3B]/20 rounded-sm">
                  <div className="w-2 h-2 rounded-full bg-[#064E3B]" />
                  <p className="text-xs text-[#064E3B] font-medium">Available for bookings</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
